/**
 * PWA Install Prompt Component
 * Shows install prompt when PWA can be installed
 * Handles beforeinstallprompt event and user installation
 */

import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { PWA_CONFIG } from '../constants/appConstants';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed using multiple methods
    // IMPORTANT: Only trust reliable detection methods, not localStorage alone
    const checkIfInstalled = async () => {
      // Method 1: Check display-mode (MOST RELIABLE - works when app is opened from installed shortcut)
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
        localStorage.setItem('pwa-installed', 'true');
        return true;
      }

      // Method 2: Check iOS standalone mode (RELIABLE)
      if (window.navigator.standalone === true) {
        setIsInstalled(true);
        localStorage.setItem('pwa-installed', 'true');
        return true;
      }

      // Method 3: Use getInstalledRelatedApps API (RELIABLE - checks actual installation)
      if ('getInstalledRelatedApps' in navigator) {
        try {
          const relatedApps = await navigator.getInstalledRelatedApps();
          if (relatedApps && relatedApps.length > 0) {
            setIsInstalled(true);
            localStorage.setItem('pwa-installed', 'true');
            return true;
          } else {
            // If getInstalledRelatedApps returns empty, app is NOT installed
            localStorage.removeItem('pwa-installed');
          }
        } catch (error) {
          console.log('getInstalledRelatedApps not supported or failed:', error);
          localStorage.removeItem('pwa-installed');
        }
      }

      // Don't trust localStorage flag alone - it might be stale after uninstall
      // Clear it if we can't verify with reliable methods
      localStorage.removeItem('pwa-installed');
      return false;
    };

    // Check installation status
    checkIfInstalled().then((installed) => {
      if (installed) {
        return; // Already installed, exit early
      }
    });

    // Check if prompt was recently dismissed
    const dismissed = localStorage.getItem(PWA_CONFIG.INSTALL_PROMPT.STORAGE_KEY);
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const now = Date.now();
      if (now - dismissedTime < PWA_CONFIG.INSTALL_PROMPT.DISMISS_DURATION) {
        return; // Don't show prompt if dismissed recently
      }
    }

    // Listen for beforeinstallprompt event
    // Note: This event ONLY fires if the app is NOT already installed
    // If this fires, clear any stale localStorage flag
    const handleBeforeInstallPrompt = (e) => {
      // Clear any stale localStorage flag - app is definitely not installed
      localStorage.removeItem('pwa-installed');
      setIsInstalled(false); // Ensure installed state is false
      // Prevent default browser install prompt
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
      // Store installation flag in localStorage for future checks
      localStorage.setItem('pwa-installed', 'true');
      toast.success('Yogvital has been installed successfully!', {
        position: 'top-center',
        autoClose: 3000,
      });
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    try {
      // Show the install prompt
      deferredPrompt.prompt();

      // Wait for user response
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        toast.success('Installing Yogvital...', {
          position: 'top-center',
          autoClose: 2000,
        });
      } else {
        console.log('User dismissed the install prompt');
        // Store dismissal time
        localStorage.setItem(
          PWA_CONFIG.INSTALL_PROMPT.STORAGE_KEY,
          Date.now().toString()
        );
      }

      // Clear the deferred prompt
      setDeferredPrompt(null);
      setShowPrompt(false);
    } catch (error) {
      console.error('Error showing install prompt:', error);
      toast.error('Failed to show install prompt', {
        position: 'top-center',
        autoClose: 3000,
      });
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDeferredPrompt(null);
    // Store dismissal time
    localStorage.setItem(
      PWA_CONFIG.INSTALL_PROMPT.STORAGE_KEY,
      Date.now().toString()
    );
  };

  // Don't show if already installed or prompt not available
  if (isInstalled || !showPrompt || !deferredPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 animate-slide-up">
      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 p-4 flex items-start gap-4">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg flex items-center justify-center">
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            Install Yogvital
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            Install our app for a better experience. Get quick access and work offline.
          </p>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleInstallClick}
              className="flex-1 bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-4 py-2 rounded-lg font-medium hover:from-teal-600 hover:to-emerald-600 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
            >
              Install
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 rounded-lg"
            >
              Later
            </button>
          </div>
        </div>

        {/* Close Button */}
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none"
          aria-label="Dismiss"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Add CSS animation */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PWAInstallPrompt;

